import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Target, Save } from "lucide-react";
import { toast } from "@/lib/safeToast";

export function DiaryTargetSymptomLog({ patientId, context }: { patientId: string; context?: string }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data: targets = [] } = useQuery({
    queryKey: ["diary-targets", patientId],
    enabled: !!patientId,
    queryFn: async () =>
      (await supabase.from("patient_target_symptoms").select("*").eq("patient_id", patientId).eq("status", "ativo").order("priority", { ascending: true })).data ?? [],
  });
  const [values, setValues] = useState<Record<string, string>>({});

  if (!patientId) return null;

  async function saveAll() {
    if (!user) return;
    const rows = Object.entries(values)
      .filter(([, v]) => v !== "" && v != null)
      .map(([target_symptom_id, v]) => {
        const target = (targets as any[]).find((t) => t.id === target_symptom_id);
        return {
          owner_id: user.id,
          patient_id: patientId,
          target_symptom_id,
          symptom_name: target?.symptom_name ?? "sintoma",
          intensity_0_10: Number(v),
          context: context || "diário do paciente",
          measured_at: new Date().toISOString(),
        };
      });
    if (rows.length === 0) return toast.error("Nada para salvar");
    const { error } = await supabase.from("symptom_measurements").insert(rows);
    if (error) return toast.error(error.message);
    toast.success(`${rows.length} medição(ões) salvas`);
    setValues({});
    qc.invalidateQueries({ queryKey: ["symptom-measurements", patientId] });
    qc.invalidateQueries({ queryKey: ["all-symptom-measurements"] });
  }

  return (
    <Card className="p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="font-medium flex items-center gap-2 text-sm">
          <Target className="h-4 w-4 text-primary" /> Sintomas-alvo agora
        </div>
        <Badge variant="outline" className="text-[10px]">0–10</Badge>
      </div>
      {(targets as any[]).length === 0 ? (
        <div className="text-xs text-muted-foreground">Nenhum sintoma-alvo ativo. Cadastre em Sintomas.</div>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
            {(targets as any[]).map((t) => (
              <div key={t.id} className="space-y-1">
                <Label className="text-[11px] uppercase tracking-wide text-muted-foreground flex items-center justify-between gap-2">
                  <span className="truncate">{t.symptom_name}</span>
                  <span className="text-[10px] normal-case">meta {t.therapeutic_goal ?? "—"}</span>
                </Label>
                <Input
                  type="number"
                  min={0}
                  max={10}
                  placeholder={`base ${t.baseline ?? "—"}`}
                  value={values[t.id] ?? ""}
                  onChange={(e) => setValues({ ...values, [t.id]: e.target.value })}
                />
              </div>
            ))}
          </div>
          <div className="flex justify-end">
            <Button size="sm" onClick={saveAll}>
              <Save className="h-3.5 w-3.5 mr-1.5" /> Registrar medições
            </Button>
          </div>
        </>
      )}
    </Card>
  );
}
